import { doc, getDoc, setDoc, increment, runTransaction } from "firebase/firestore";
import { db } from "../firebase";

// 1. Generate or retrieve persistent Device ID from localStorage
export const getDeviceId = () => {
  let deviceId = localStorage.getItem("portfolio_device_id");
  if (!deviceId) {
    // Generate unique device ID
    deviceId = "dev_" + Math.random().toString(36).substring(2, 11) + Date.now().toString(36);
    localStorage.setItem("portfolio_device_id", deviceId);
  }
  return deviceId;
};

// 2. Format number to human-readable string (e.g. 128 -> 128, 2400 -> 2.4K, 1500000 -> 1.5M)
export const formatCount = (num) => {
  if (!num || isNaN(num)) return "0";
  if (num >= 1000000) {
    return (num / 1000000).toFixed(1).replace(/\.0$/, "") + "M";
  }
  if (num >= 1000) {
    return (num / 1000).toFixed(1).replace(/\.0$/, "") + "K";
  }
  return num.toString();
};

// 3. Check if current device has liked a specific project
export const checkIsLiked = async (projectId) => {
  if (!projectId) return false;
  const deviceId = getDeviceId();
  const likeDocRef = doc(db, "likes", `${projectId}_${deviceId}`);

  try {
    const snap = await getDoc(likeDocRef);
    return snap.exists();
  } catch (err) {
    console.error(`Error checking like for ${projectId}:`, err);
    return false;
  }
};

// 4. Like a project (Device-Based Single Like with Firestore Transaction)
export const likeProject = async (projectId) => {
  if (!projectId) return { success: false, message: "Invalid project ID" };

  const deviceId = getDeviceId();
  const likeDocRef = doc(db, "likes", `${projectId}_${deviceId}`);
  const projectDocRef = doc(db, "projects", projectId);

  try {
    // Check if like record already exists for this device
    const likeSnap = await getDoc(likeDocRef);
    if (likeSnap.exists()) {
      return {
        success: false,
        isLiked: true,
        alreadyLiked: true,
        message: "You have already liked this project!"
      };
    }

    // Run Firestore transaction to ensure atomic increment and document creation
    await runTransaction(db, async (transaction) => {
      const projectSnap = await transaction.get(projectDocRef);
      
      // 1. Create document inside 'likes' collection
      transaction.set(likeDocRef, {
        projectId,
        deviceId,
        likedAt: new Date().toISOString()
      });

      // 2. Increment likes count in 'projects' collection
      if (projectSnap.exists()) {
        const currentLikes = projectSnap.data().likes || 0;
        transaction.update(projectDocRef, { likes: currentLikes + 1 });
      } else {
        transaction.set(projectDocRef, { likes: 1, views: 0 }, { merge: true });
      }
    });

    return {
      success: true,
      isLiked: true,
      alreadyLiked: false,
      message: "Project liked successfully!"
    };

  } catch (error) {
    console.error("Firestore Transaction Error in likeProject:", error);
    
    // Fallback if transaction fails
    try {
      await setDoc(likeDocRef, {
        projectId,
        deviceId,
        likedAt: new Date().toISOString()
      });
      await setDoc(projectDocRef, { likes: increment(1) }, { merge: true });
      return { success: true, isLiked: true, alreadyLiked: false };
    } catch (fallbackError) {
      console.error("Fallback error in likeProject:", fallbackError);
      return { success: false, message: "Failed to update like count" };
    }
  }
};

// 5. Increment views once per device using localStorage
export const trackProjectView = async (projectId) => {
  if (!projectId) return;

  const storageKey = `viewed_${projectId}`;
  const alreadyViewed = localStorage.getItem(storageKey);

  if (alreadyViewed === "true") {
    // Already viewed by this device, do not increment again on refresh
    return;
  }

  // Mark as viewed in localStorage
  localStorage.setItem(storageKey, "true");

  try {
    const projectDocRef = doc(db, "projects", projectId);
    await setDoc(projectDocRef, { views: increment(1) }, { merge: true });
  } catch (err) {
    console.error(`Error incrementing views for ${projectId}:`, err);
  }
};
